import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, TextField } from '@mui/material';
import { useCreateOrganizationMutation } from '@/state/mutations/organizations/use-create-organization.ts';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { createOrganizationSchemaFactory } from '@/schemas/organizations/create-organization';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useNavigate } from 'react-router';
import { useUpdateUserMutation } from '@/state/mutations/users/use-update-user';

interface FormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export default function CreateForm({ onCancel, onSuccess }: FormProps) {
  const notification = useNotifications();
  const t = useTranslations();
  const { setActiveOrganization } = useActiveOrganizationStore();
  const { mutate: updateUserMutation } = useUpdateUserMutation();
  const navigate = useNavigate();

  const createOrganizationSchema = createOrganizationSchemaFactory(t);
  type CreateOrganization = z.infer<typeof createOrganizationSchema>;
  const { control, handleSubmit } = useForm<CreateOrganization>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: '',
    },
  });
  const { mutate, data, isSuccess, isPending, error } = useCreateOrganizationMutation();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (isSuccess && data) {
      onSuccessRef.current();
      notification.notifySuccess(t('organization.createSuccess', { name: data.name }));
      setActiveOrganization(data);

      updateUserMutation({
        activeOrganization: data.id,
      });
      const currentPath = window.location.pathname;
      const newPath = currentPath.includes('/organization/')
        ? currentPath.replace(/\/organization\/[^/]+/, `/organization/${data.id}`)
        : currentPath;
      navigate(newPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when mutation succeeds; other deps cause infinite loop
  }, [isSuccess, data]);

  useEffect(() => {
    if (error) {
      notification.notifyError(t('organization.createError'));
    }
  }, [error]);

  const onSubmit = (data: { name: string }) => {
    mutate(data);
  };

  return (
    <Box
      noValidate
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        mt: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            required
            fullWidth
            type="text"
            variant="standard"
            label={t('organization.name')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
          />
        )}
      />
      <Box
        sx={{
          display: 'flex',
          mt: 2,
          gap: 2,
          width: '100%',
          justifyContent: 'end',
        }}
      >
        <Button variant="outlined" size="small" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="contained" size="small" loading={isPending}>
          {t('common.create')}
        </Button>
      </Box>
    </Box>
  );
}
